import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { getToken } from '../../utils/tokenUtils';
import { getRolePrefix } from '../../utils/routeUtils';
import { AuthContext } from '../../contexts/AuthContext';
import {
  ArrowLeft,
  Search,
  Edit,
  CheckCircle,
  X,
  RefreshCw,
  Mail,
  Download,
  HelpCircle,
  Bell,
  User,
} from 'lucide-react';
import StatusBadge from '../../components/UI/StatusBadge';
import Notification from '../../components/UI/Notification';

const PayrollDetailsView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { roles } = useContext(AuthContext);
  const token = getToken();
  const rolePrefix = getRolePrefix(roles);
  
  const [payroll, setPayroll] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notification, setNotification] = useState(null);
  const [activeTab, setActiveTab] = useState('earnings');

  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  useEffect(() => {
    fetchPayrollDetails();
  }, [id]);

  const fetchPayrollDetails = async () => {
    try {
      setLoading(true);
      const response = await fetch(
        `https://hrmspayroll.prepstripe.com/api/payroll/monthly-payrolls/${id}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      const data = await response.json();
      setPayroll(data?.data || data);
    } catch (error) {
      console.error('Error fetching payroll details:', error);
      setNotification({
        type: 'error',
        message: 'Failed to fetch payroll details',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async () => {
    try {
      const response = await fetch(
        `https://hrmspayroll.prepstripe.com/api/payroll/monthly-payrolls/${id}`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
          body: JSON.stringify({ status: 'approved' })
        }
      );
      
      if (response.ok) {
        setNotification({
          type: 'success',
          message: 'Payroll approved successfully',
        });
        fetchPayrollDetails();
      }
    } catch (error) {
      setNotification({
        type: 'error',
        message: 'Failed to approve payroll',
      });
    }
  };

  const handleReject = async () => {
    try {
      const response = await fetch(
        `https://hrmspayroll.prepstripe.com/api/payroll/monthly-payrolls/${id}`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
          body: JSON.stringify({ status: 'rejected' })
        }
      );
      
      if (response.ok) {
        setNotification({
          type: 'success',
          message: 'Payroll rejected successfully',
        });
        fetchPayrollDetails();
      }
    } catch (error) {
      setNotification({
        type: 'error',
        message: 'Failed to reject payroll',
      });
    }
  };

  const handleRegenerate = async () => {
    if (!window.confirm('Are you sure you want to regenerate this payroll?')) {
      return;
    }
    
    try {
      const response = await fetch(
        `https://hrmspayroll.prepstripe.com/api/payroll/regenerate-employee-payroll`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
          body: JSON.stringify({
            employee_id: payroll?.employee_id,
            year: payroll?.payroll_year,
            month: payroll?.payroll_month,
          })
        }
      );
      
      if (response.ok) {
        setNotification({
          type: 'success',
          message: 'Payroll regenerated successfully',
        });
        fetchPayrollDetails();
      }
    } catch (error) {
      setNotification({
        type: 'error',
        message: 'Failed to regenerate payroll',
      });
    }
  };

  const handleSendPayslip = async () => {
    try {
      // TODO: Implement send payslip API call
      setNotification({
        type: 'success',
        message: 'Payslip sent successfully',
      });
    } catch (error) {
      setNotification({
        type: 'error',
        message: 'Failed to send payslip',
      });
    }
  };

  const handleDownload = async () => {
    try {
      const response = await fetch(
        `https://hrmspayroll.prepstripe.com/api/payroll/monthly-payrolls/${id}/download`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
          }
        }
      );
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `payslip-${payroll?.employee_id}-${payroll?.payroll_month}-${payroll?.payroll_year}.pdf`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      }
    } catch (error) {
      setNotification({
        type: 'error',
        message: 'Failed to download payslip',
      });
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!payroll) {
    return (
      <div className="p-6">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-12 text-center">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Payroll Not Found</h3>
        </div>
      </div>
    );
  }

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    } catch {
      return dateString;
    }
  };

  return (
    <div className="p-6 bg-gray-50 dark:bg-gray-900 min-h-screen">
      {notification && (
        <Notification
          message={notification.message}
          type={notification.type}
          onClose={() => setNotification(null)}
        />
      )}

      {/* Top Header */}
      <div className="mb-6">
        {/* First Line: Title and Employee Details */}
        <div className="mb-4">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
            Payroll Details
          </h1>
          <div className="mt-2 flex gap-x-3 items-center">
            <span>•</span><p className="text-gray-600 dark:text-gray-400">
              {payroll.employee_id || 'N/A'}
            </p>
            <p className="text-gray-600 dark:text-gray-400">
              {months[payroll.payroll_month - 1]}
            </p>
            <p className="text-gray-600 dark:text-gray-400">
              {payroll.payroll_year}
            </p>
          </div>
        </div>
        
        {/* Second Line: Search and Action Buttons */}
        <div className="flex items-center gap-3">
          <div className="relative">
            <input
              type="text"
              placeholder="Search employees, payrolls..."
              className="pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-800 dark:text-white w-64"
            />
            <Search
              className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400"
            />
          </div>
          <button
            onClick={() => navigate(`${rolePrefix}/payroll/processing/details/${id}/edit`)}
            className="flex items-center gap-2 px-4 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 text-gray-700 dark:text-gray-300 rounded-lg font-medium hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
          >
            <Edit className="h-4 w-4" />
            Edit
          </button>
          <button
            onClick={handleApprove}
            className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium transition-colors"
          >
            <CheckCircle className="h-4 w-4" />
            Approve
          </button>
          <button
            onClick={handleReject}
            className="flex items-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg font-medium transition-colors"
          >
            <X className="h-4 w-4" />
            Reject
          </button>
          <button
            onClick={handleRegenerate}
            className="flex items-center gap-2 px-4 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 text-gray-700 dark:text-gray-300 rounded-lg font-medium hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
          >
            <RefreshCw className="h-4 w-4" />
            Regenerate
          </button>
          <button
            onClick={handleSendPayslip}
            className="flex items-center gap-2 px-4 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 text-gray-700 dark:text-gray-300 rounded-lg font-medium hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
          >
            <Mail className="h-4 w-4" />
            Send Payslip
          </button>
          <button
            onClick={handleDownload}
            className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium transition-colors"
          >
            <Download className="h-4 w-4" />
            Download
          </button>
          
        </div>
      </div>
      <div className='pb-4'>
      <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
            Back
          </button>
      </div>
      
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Panel */}
        <div className="lg:col-span-1 space-y-6">
          {/* Back Button */}
         

          {/* Employee Details Card */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                Employee Details
              </h2>
              <StatusBadge status={payroll.status} />
            </div>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-full bg-blue-100 dark:bg-blue-900 flex items-center justify-center">
                <User className="h-6 w-6 text-blue-600 dark:text-blue-400" />
              </div>
              <div>
                <p className="font-semibold text-gray-900 dark:text-white">
                  {payroll.employee_name || 'N/A'}
                </p>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {payroll.employee_id}
                </p>
              </div>
            </div>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-400">Department:</span>
                <span className="text-gray-900 dark:text-white font-medium">
                  {payroll.department || payroll.department_name || 'N/A'}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-400">Designation:</span>
                <span className="text-gray-900 dark:text-white font-medium">
                  {payroll.designation || 'N/A'}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-400">Period:</span>
                <span className="text-gray-900 dark:text-white font-medium">
                  {months[payroll.payroll_month - 1]} {payroll.payroll_year}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-400">Payment Date:</span>
                <span className="text-gray-900 dark:text-white font-medium">
                  {formatDate(payroll.salary_date || payroll.payment_date)}
                </span>
              </div>
            </div>
          </div>

          {/* Salary Summary Card */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Salary Summary
            </h2>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-400">Basic Salary:</span>
                <span className="text-gray-900 dark:text-white font-semibold">
                  ₹{payroll.basic_salary?.toLocaleString() || '0'}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-400">Gross Salary:</span>
                <span className="text-gray-900 dark:text-white font-semibold">
                  ₹{payroll.gross_salary?.toLocaleString() || '0'}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-400">Total Deductions:</span>
                <span className="text-red-600 dark:text-red-400 font-semibold">
                  -₹{payroll.total_deductions?.toLocaleString() || '0'}
                </span>
              </div>
              <div className="flex justify-between pt-3 border-t border-gray-200 dark:border-gray-700">
                <span className="text-gray-900 dark:text-white font-semibold">Net Salary:</span>
                <span className="text-blue-600 dark:text-blue-400 font-bold text-lg">
                  ₹{payroll.net_salary?.toLocaleString() || '0'}
                </span>
              </div>
            </div>
          </div>

          {/* Attendance Summary Card */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Attendance Summary
            </h2>
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg p-4 text-center">
                <p className="text-2xl font-bold text-gray-900 dark:text-white">
                  {payroll.working_days || payroll.total_working_days || 0}
                </p>
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">Working Days</p>
              </div>
              <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4 text-center">
                <p className="text-2xl font-bold text-green-600 dark:text-green-400">
                  {payroll.days_present || 0}
                </p>
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">Days Present</p>
              </div>
              <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 text-center">
                <p className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                  {payroll.leave_days || payroll.days_on_leave || 0}
                </p>
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">Days on Leave</p>
              </div>
              <div className="bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-lg p-4 text-center">
                <p className="text-2xl font-bold text-orange-600 dark:text-orange-400">
                  {payroll.overtime_hours || payroll.ot_hours || 0}
                </p>
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">OT Hours</p>
              </div>
            </div>
          </div>
        </div>

        {/* Right Panel */}
        <div className="lg:col-span-2">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm">
            {/* Tabs */}
            <div className="border-b border-gray-200 dark:border-gray-700">
              <div className="flex space-x-1 px-6">
                {['earnings', 'deductions', 'variable', 'attendance', 'audit-trail'].map((tab) => (
                  <button
                    key={tab}
                    onClick={() => setActiveTab(tab)}
                    className={`px-4 py-3 text-sm font-medium transition-colors ${
                      activeTab === tab
                        ? 'text-green-600 dark:text-green-400 border-b-2 border-green-600 dark:border-green-400'
                        : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                    }`}
                  >
                    {tab.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}
                  </button>
                ))}
              </div>
            </div>

            {/* Tab Content */}
            <div className="p-6">
              {activeTab === 'earnings' && (
                <div>
                  <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                    <thead className="bg-gray-50 dark:bg-gray-900">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                          Component
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                          Amount
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                          Payable
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                          Status
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                      {payroll.earnings_breakdown && Array.isArray(payroll.earnings_breakdown) ? (
                        payroll.earnings_breakdown.map((earning, index) => (
                          <tr key={index}>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                              {earning.name || earning.component || 'N/A'}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300">
                              ₹{earning.amount?.toLocaleString() || '0'}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300">
                              ₹{earning.payable || earning.amount?.toLocaleString() || '0'}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <StatusBadge status={earning.status || payroll.status} />
                            </td>
                          </tr>
                        ))
                      ) : payroll.earnings_breakdown && typeof payroll.earnings_breakdown === 'object' ? (
                        Object.entries(payroll.earnings_breakdown).map(([name, amount], index) => (
                          <tr key={index}>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                              {name}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300">
                              ₹{typeof amount === 'number' ? amount.toLocaleString() : '0'}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300">
                              ₹{typeof amount === 'number' ? amount.toLocaleString() : '0'}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <StatusBadge status={payroll.status} />
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="4" className="px-6 py-4 text-center text-gray-500">
                            No earnings data available
                          </td>
                        </tr>
                      )}
                      <tr className="bg-gray-50 dark:bg-gray-900 font-semibold">
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                          Total Earnings
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                          ₹{payroll.gross_salary?.toLocaleString() || payroll.total_earnings?.toLocaleString() || '0'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                          ₹{payroll.gross_salary?.toLocaleString() || payroll.total_earnings?.toLocaleString() || '0'}
                        </td>
                        <td></td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              )}

              {activeTab === 'deductions' && (
                <div>
                  <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                    <thead className="bg-gray-50 dark:bg-gray-900">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                          Component
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                          Amount
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                          Deductible
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                          Status
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                      {payroll.deductions_breakdown && Array.isArray(payroll.deductions_breakdown) ? (
                        payroll.deductions_breakdown.map((deduction, index) => (
                          <tr key={index}>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                              {deduction.name || deduction.component || 'N/A'}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300">
                              ₹{deduction.amount?.toLocaleString() || '0'}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-red-600 dark:text-red-400">
                              -₹{deduction.deductible || deduction.amount?.toLocaleString() || '0'}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <StatusBadge status={deduction.status || payroll.status} />
                            </td>
                          </tr>
                        ))
                      ) : payroll.deductions_breakdown && typeof payroll.deductions_breakdown === 'object' ? (
                        Object.entries(payroll.deductions_breakdown).map(([name, amount], index) => (
                          <tr key={index}>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                              {name}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300">
                              ₹{typeof amount === 'number' ? amount.toLocaleString() : '0'}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-red-600 dark:text-red-400">
                              -₹{typeof amount === 'number' ? amount.toLocaleString() : '0'}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <StatusBadge status={payroll.status} />
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="4" className="px-6 py-4 text-center text-gray-500">
                            No deductions data available
                          </td>
                        </tr>
                      )}
                      <tr className="bg-gray-50 dark:bg-gray-900 font-semibold">
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                          Total Deductions
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                          ₹{payroll.total_deductions?.toLocaleString() || '0'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-red-600 dark:text-red-400">
                          -₹{payroll.total_deductions?.toLocaleString() || '0'}
                        </td>
                        <td></td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              )}

              {activeTab === 'variable' && (
                <div>
                  <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                    <thead className="bg-gray-50 dark:bg-gray-900">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                          Component
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                          Amount
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                          Status
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                      <tr>
                        <td colSpan="3" className="px-6 py-4 text-center text-gray-500">
                          No variable earnings data available
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              )}

              {activeTab === 'attendance' && (
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg p-6 text-center">
                    <p className="text-3xl font-bold text-gray-900 dark:text-white">
                      {payroll.working_days || payroll.total_working_days || 0}
                    </p>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">Total Working Days</p>
                  </div>
                  <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-6 text-center">
                    <p className="text-3xl font-bold text-green-600 dark:text-green-400">
                      {payroll.days_present || 0}
                    </p>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">Days Present</p>
                  </div>
                  <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-6 text-center">
                    <p className="text-3xl font-bold text-blue-600 dark:text-blue-400">
                      {payroll.leave_days || payroll.days_on_leave || 0}
                    </p>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">Days on Leave</p>
                  </div>
                  <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg p-6 text-center">
                    <p className="text-3xl font-bold text-gray-900 dark:text-white">
                      {payroll.lop_days || 0}
                    </p>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">Days LWP</p>
                  </div>
                  <div className="bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-lg p-6 text-center">
                    <p className="text-3xl font-bold text-orange-600 dark:text-orange-400">
                      {payroll.overtime_hours || payroll.ot_hours || 0}
                    </p>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">Overtime Hours</p>
                  </div>
                  <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-6 text-center">
                    <p className="text-3xl font-bold text-green-600 dark:text-green-400">
                      {payroll.attendance_percentage || 
                        (payroll.days_present && payroll.working_days 
                          ? ((payroll.days_present / payroll.working_days) * 100).toFixed(1) 
                          : '0')}%
                    </p>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">Attendance %</p>
                  </div>
                </div>
              )}

              {activeTab === 'audit-trail' && (
                <div className="space-y-4">
                  {payroll.audit_trail && payroll.audit_trail.length > 0 ? (
                    payroll.audit_trail.map((audit, index) => (
                      <div key={index} className="flex items-start gap-4 pb-4 border-b border-gray-200 dark:border-gray-700 last:border-0">
                        <div className="w-2 h-2 bg-green-500 rounded-full mt-2"></div>
                        <div className="flex-1">
                          <p className="font-semibold text-gray-900 dark:text-white capitalize">
                            {audit.action || audit.status}
                          </p>
                          <p className="text-sm text-gray-600 dark:text-gray-400">
                            By: {audit.by || audit.user || 'System'}
                          </p>
                          {audit.note && (
                            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                              Note: {audit.note}
                            </p>
                          )}
                          <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                            {audit.timestamp || audit.created_at || 'N/A'}
                          </p>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="text-center py-8 text-gray-500">
                      No audit trail data available
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PayrollDetailsView;

